"use client";

import Image from "next/image";
import Link from "next/link";
import { renderToStaticMarkup } from "react-dom/server";
import { useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { useMembership } from "@/lib/hooks/useMembership";
import { trackEvent } from "@/lib/analytics";
import { track } from "@vercel/analytics";
import { StoryblokComponent, storyblokEditable } from "@storyblok/react/rsc";
import { renderRichText } from "@storyblok/react";
import { renderTableCell } from "@/lib/storyblok/tableCell";
import type { ArticleStoryblok } from "@/component-types-sb";
import { MemberLockIcon } from "@/components/icons/MemberLockIcon";
import { ArticleTag } from "@/components/layout/ArticleTag";
import SingleEventCard from "@/components/storyblok/SingleEventCard";

import { Button } from "@/components/ui/button";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { Separator } from "@/components/ui/separator";
import { Headline } from "@/components/typography/Headline";
import { RichText } from "@/components/typography/RichText";
import ArticleGallery from "@/components/storyblok/ArticleGallery";
import Cta from "@/components/storyblok/Cta";
import InfoBox from "@/components/storyblok/InfoBox";
import ImageTeaser from "@/components/storyblok/ImageTeaser";
import Ad from "@/components/storyblok/Ad";
import AdSpace from "@/components/storyblok/AdSpace";
import Accordion from "@/components/storyblok/Accordion";
import TestimonialSection from "@/components/storyblok/TestimonialSection";
import TextImage from "@/components/storyblok/TextImage";
import VideoEmbed from "@/components/storyblok/VideoEmbed";
import CtaSection from "@/components/storyblok/CtaSection";
import { formatDate } from "@/lib/utils/date";
import { cn } from "@/lib/utils";
import { setReturnIntent } from "@/lib/auth/return-intent";

type ArticleProps = {
  blok: ArticleStoryblok;
  createdAt: string;
};

const Article = ({ blok, createdAt }: ArticleProps) => {
  const pathname = usePathname();
  const { userId } = useAuth();
  const { isMember } = useMembership();
  const accessDenied = (blok as any)._access_denied === true;
  const isLoggedIn = !!userId;

  useEffect(() => {
    trackEvent("content_viewed", {
      type: "article",
      title: blok.title,
      slug: pathname,
    });
  }, [blok.title, pathname]);

  // Intercept TOC anchor clicks when article is locked — scroll to member CTA instead
  useEffect(() => {
    if (!accessDenied) return;
    const handleTocClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('#toc a[href^="#"]');
      if (!link) return;
      const anchor = link.getAttribute("href")?.slice(1);
      if (anchor && !document.getElementById(anchor)) {
        e.preventDefault();
        document.getElementById("member-cta")?.scrollIntoView({ behavior: "smooth" });
      }
    };
    document.addEventListener("click", handleTocClick);
    return () => document.removeEventListener("click", handleTocClick);
  }, [accessDenied]);

  // Track clicks on inline ads rendered via renderToStaticMarkup (no React hydration)
  useEffect(() => {
    const handleAdClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest<HTMLAnchorElement>("[data-ad-name]");
      if (!link) return;
      track("Ad click", {
        uid: link.dataset.adUid,
        name: link.dataset.adName,
        campaign: link.dataset.adCampaign,
      });
      trackEvent("ad_clicked", {
        name: link.dataset.adName,
        campaign: link.dataset.adCampaign,
        url: link.dataset.adUrl,
      });
    };
    document.addEventListener("click", handleAdClick);
    return () => document.removeEventListener("click", handleAdClick);
  }, []);

  const richTextHtml = useMemo(
    () =>
      renderRichText(blok.text, {
        resolver: (component, blok) => {
          switch (component) {
            case "cta":
              return renderToStaticMarkup(
                <div className="not-prose max-w-xl mx-auto my-8 text-center">
                  <Cta blok={blok} location="article_cta" />
                </div>
              );
            case "info_box":
              return renderToStaticMarkup(<InfoBox blok={blok} />);
            case "image_teaser":
              return renderToStaticMarkup(
                <div className="not-prose">
                  <ImageTeaser blok={blok} />
                </div>
              );
            case "ad":
              if (isMember) return "";
              return renderToStaticMarkup(<Ad blok={blok} />);
            case "adspace":
              if (isMember) return "";
              return renderToStaticMarkup(<AdSpace blok={blok} />);
            case "accordion":
              return renderToStaticMarkup(
                <div className="not-prose">
                  <Accordion blok={blok} />
                </div>
              );
            // Placeholder like single_event_card below: a nested
            // renderToStaticMarkup breaks useCarousel() during SSR.
            case "testimonial_section":
              return `<!--TST:${btoa(encodeURIComponent(JSON.stringify(blok)))}:TST-->`;
            case "faq_section":
              return "";
            case "table":
              return `<table>
                <thead>
                  <tr>
                    ${blok.table.thead
                      .map((th: any) => `<th>${renderTableCell(th.value)}</th>`.trim())
                      .join("")}
                  </tr>
                </thead>
                <tbody>
                  ${blok.table.tbody
                    .map(
                      (tr: any) =>
                        `<tr>${tr.body
                          .map((td: any) => `<td>${renderTableCell(td.value)}</td>`.trim())
                          .join("")}</tr>`
                    )
                    .join("")}
                </tbody>
              </table>`;
            case "text_image":
              return renderToStaticMarkup(
                <div className="not-prose">
                  <TextImage blok={blok} />
                </div>
              );
            case "video_embed":
              if (!blok.playback_id) return "";
              return renderToStaticMarkup(<VideoEmbed blok={blok} />);
            case "single_event_card":
              return `<!--SEC:${btoa(encodeURIComponent(JSON.stringify(blok)))}:SEC-->`;
            case "cta_section":
              return `<!--CTA:${btoa(encodeURIComponent(JSON.stringify(blok)))}:CTA-->`;
            default:
              return "";
          }
        },
      }),
    [blok.text, isMember]
  );

  type RichTextSegment =
    | { type: "html"; html: string }
    | { type: "event-card"; blok: any }
    | { type: "cta-section"; blok: any }
    | { type: "testimonial"; blok: any };

  const richTextSegments = useMemo(() => {
    const html = richTextHtml;
    if (
      !html ||
      (!html.includes("<!--SEC:") &&
        !html.includes("<!--CTA:") &&
        !html.includes("<!--TST:"))
    ) {
      return [{ type: "html" as const, html: html || "" }];
    }

    const segments: RichTextSegment[] = [];
    const parts = html.split(
      /(<!--SEC:.*?:SEC-->|<!--CTA:.*?:CTA-->|<!--TST:.*?:TST-->)/
    );
    for (const part of parts) {
      if (!part) continue;
      const secMatch = part.match(/^<!--SEC:(.*?):SEC-->$/);
      if (secMatch) {
        try {
          const blokData = JSON.parse(decodeURIComponent(atob(secMatch[1])));
          segments.push({ type: "event-card", blok: blokData });
        } catch (err) {
          console.error("Failed to parse SingleEventCard blok:", err);
        }
        continue;
      }
      const ctaMatch = part.match(/^<!--CTA:(.*?):CTA-->$/);
      if (ctaMatch) {
        try {
          const blokData = JSON.parse(decodeURIComponent(atob(ctaMatch[1])));
          segments.push({ type: "cta-section", blok: blokData });
        } catch (err) {
          console.error("Failed to parse CtaSection blok:", err);
        }
        continue;
      }
      const tstMatch = part.match(/^<!--TST:(.*?):TST-->$/);
      if (tstMatch) {
        try {
          const blokData = JSON.parse(decodeURIComponent(atob(tstMatch[1])));
          segments.push({ type: "testimonial", blok: blokData });
        } catch (err) {
          console.error("Failed to parse TestimonialSection blok:", err);
        }
        continue;
      }
      segments.push({ type: "html", html: part });
    }
    return segments;
  }, [richTextHtml]);

  const hasSidebar =
    Array.isArray(blok.sidebar) &&
    blok.sidebar.filter((item) => item.component !== "ad_space").length > 0;

  return (
    <div
      className="flex flex-col justify-between px-4 pt-8 pb-16 lg:pt-16 lg:pb-24 mx-auto max-w-screen-xl"
      {...storyblokEditable(blok)}
    >
      <div
        className={cn({
          ["grid md:grid-cols-3"]: hasSidebar,
        })}
      >
        {hasSidebar && (
          <aside className="col-span-1 top-12 md:order-last">
            {blok.sidebar?.map((nestedBlok) => (
              <div
                key={nestedBlok._uid}
                className={cn(
                  nestedBlok.component === "ad_space" && "hidden lg:block"
                )}
              >
                <StoryblokComponent blok={nestedBlok} />
              </div>
            ))}
          </aside>
        )}

        <article
          className={cn(
            "mx-auto w-full max-w-3xl format format-sm sm:format-base lg:format-lg format-blue text-black-100",
            {
              ["col-span-2"]: hasSidebar,
            }
          )}
        >
          <header className="mb-4 lg:mb-6 not-format">
            <p className="text-subline-sm">
              <time dateTime={createdAt}>
                {formatDate(new Date(createdAt))}
              </time>
            </p>
            <Headline
              as="h1"
              size="tall"
              className="mb-4 lg:mb-6"
            >
              {blok.title}
            </Headline>

            {/* Lock icon + Tags */}
            {(blok.member_access === "member_only" || (blok.tags && blok.tags.length > 0)) && (
              <div className="flex items-center gap-2 flex-wrap">
                {blok.member_access === "member_only" && (
                  <MemberLockIcon locked={accessDenied} />
                )}
                {blok.tags?.map((tag) => (
                  <ArticleTag key={String(tag)} label={String(tag)} className="bg-white" />
                ))}
              </div>
            )}
          </header>

          <p className="text-lead mb-8">{blok.subtitle}</p>

          {blok.image?.filename && (
            <Image
              src={blok.image.filename}
              alt={blok.image.alt || ""}
              width={1280}
              height={720}
              className="mb-4 rounded-2xl"
            />
          )}

          {blok.header?.map((nestedBlok) => (
            <StoryblokComponent
              blok={nestedBlok}
              key={nestedBlok._uid}
            />
          ))}

          <div className={cn(accessDenied && "relative overflow-hidden")}>
            {richTextSegments.map((seg, i) => {
              switch (seg.type) {
                case "html":
                  return <RichText key={i} html={seg.html} />;
                case "event-card":
                  return (
                    <div key={i} className="not-prose">
                      <SingleEventCard blok={seg.blok} inline />
                    </div>
                  );
                case "testimonial":
                  return (
                    <div key={i} className="not-prose">
                      <TestimonialSection blok={seg.blok} size="regular" />
                    </div>
                  );
                default:
                  return (
                    <div key={i} className="not-prose">
                      <CtaSection blok={seg.blok} inline />
                    </div>
                  );
              }
            })}

            {/* Fade-out gradient for member-only preview */}
            {accessDenied && (
              <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-sand-10 to-transparent pointer-events-none" />
            )}
          </div>

          {/* Member-only CTA */}
          {accessDenied && (
            <div id="member-cta" className="not-prose flex flex-col items-center gap-4 py-10 px-6 my-8 bg-sky-50 rounded-3xl text-center">
              <MemberLockIcon locked />
              <h3 className="text-headline-xl text-black">
                Dieser Artikel ist exklusiv für Mitglieder
              </h3>
              <p className="text-copy text-black">
                Als Dialetics-Mitglied erhältst du Zugang zu allen TopicTalks, Sprechstunden, Community Weeks und vielen weiteren Events. Dazu kommen exklusive Artikel, Aufzeichnungen und eine Community, die dich versteht.
              </p>
              <Button variant="sun" asChild>
                <Link
                  href={isLoggedIn ? "/onboarding/membership" : `/login?redirect=${encodeURIComponent(pathname)}`}
                  onClick={() => { if (!isLoggedIn) setReturnIntent(pathname, "article_access"); }}
                  data-cta-location="article_member_cta"
                  data-cta-label="Mitglied werden"
                >
                  Mitglied werden
                  <ArrowRight />
                </Link>
              </Button>
              <Link
                href="/membership"
                className="text-copy text-black hover:opacity-70 transition-opacity"
              >
                Mehr zur Membership
              </Link>
            </div>
          )}

          {!accessDenied && blok.sponsor?.length && (
            <>
              <Separator className="my-4" />
              <h4 className="text-sm font-medium leading-none mb-4">Sponsor</h4>

              {blok.logo?.filename && (
                <Image
                  src={blok.logo.filename}
                  alt={blok.logo.alt || ""}
                  width={200}
                  height={200}
                  className="float-end ml-4 mb-4"
                />
              )}

              <RichText html={renderRichText(blok.sponsor)} />
            </>
          )}
        </article>
      </div>

      <ArticleGallery
        blok={{
          _uid: "weitere-artikel",
          component: "article_gallery",
          title: "Weitere Artikel",
          count: 6,
        }}
        excludeUid={blok._uid}
        titleAlign="left"
      />

      <div className="mt-32">
        {blok.body?.map((nestedBlok) => (
          <StoryblokComponent
            blok={nestedBlok}
            key={nestedBlok._uid}
          />
        ))}
      </div>
    </div>
  );
};

export default Article;
